import React, {  Fragment } from "react";
import { useTable, useBlockLayout } from "react-table";
import { formatValue } from "./Utils";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Tooltip from "./form-components/Tooltip.jsx";
dayjs.extend(isoWeek);


const headerClass = "th uppercase text-sm flex-row flex justify-end mr-3";

function determineReportingPeriods(data, granularity) {
	if (data === undefined) {
		return [];
	}
	
	const allMonths = [];
	const dates = [...new Set(data.map(row => dayjs(row.time).format("YYYY-MM-DD")))];
	const minDate = new Date(Math.min(...dates.map(d => new Date(d))));
	const maxDate = new Date(Math.max(...dates.map(d => new Date(d))));

	let currentDate = new Date(minDate);
	
	while (currentDate <= maxDate) {
		allMonths.push(dayjs(currentDate).format("YYYY-MM-DD"));
		currentDate = dayjs(currentDate).add(1, granularity).toDate();
		
	}

	return allMonths;
}

function getReportingPeriodIndex(reportingPeriods, date) {
	return reportingPeriods.findIndex(period => period === dayjs(date).format("YYYY-MM-DD"));
}

function addTotalColumn(pivotedData, includeForecast = false) {
	const totalCosts = pivotedData.map(row => {
		// Get all keys that start with 'C'
		const costColumns = Object.keys(row).filter(key => key.startsWith('C') && !key.includes('_'));
		
		let totalMeasure = 0;
		let totalForecast = 0;
		
		if (includeForecast) {
			// Calculate separate totals for measure and forecast
			costColumns.forEach(key => {
				const measureValue = row[`${key}_measure`] || 0;
				const forecastValue = row[`${key}_forecast`] || 0;
				totalMeasure += measureValue;
				totalForecast += forecastValue;
			});
			
			return {
				...row,
				total: totalMeasure + totalForecast,
				total_measure: totalMeasure,
				total_forecast: totalForecast,
				total_type: 'combined'
			};
		} else {
			// Calculate total from measure values only
			const total = costColumns.reduce((sum, key) => {
				return sum + (row[key] || 0);
			}, 0);
			
			return {
				...row,
				total: total,
				total_measure: total,
				total_forecast: 0,
				total_type: 'measure'
			};
		}
	});

	return totalCosts;
}   

function pivotData(data, reportingPeriods, includeForecast = false) {
	if (data === undefined) {
		return [];
	}
	
	// Step 1: Group data by unique keys (allocation_type, allocation_method, allocated_from)
	const grouped = {};
		
	data.forEach(row => {
		const key = `${row.grouping}`;
		
		if (!grouped[key]) {
			grouped[key] = { 
				grouping: row.grouping,
			};
		}
	   
		// Add the date as a column with the allocated_costs value
		const dateKey = "C" + (getReportingPeriodIndex(reportingPeriods, row.time) + 1);

		if (!includeForecast) {
			grouped[key][dateKey] = row.measure;
			grouped[key][`${dateKey}_type`] = 'measure';
		} else {
			grouped[key][dateKey] = row.measure + row.forecast;
			// Store the breakdown for coloring
			grouped[key][`${dateKey}_measure`] = row.measure;
			grouped[key][`${dateKey}_forecast`] = row.forecast;
			grouped[key][`${dateKey}_type`] = 'combined';
		}
	});

	// Step 2: Convert grouped object back to an array
	return Object.values(grouped);
}

const valueCell = (cell, includeForecast = false) => {
	const columnId = cell.column.id;
	const rowData = cell.row.original;
	
	// Determine if this is a measure or forecast value
			
	if (columnId === 'total') {
		// For total column, show the combined sum as a single value
		const rawValue = cell.value || 0;
		const roundedValue = Math.round((rawValue + Number.EPSILON) * 100) / 100;
		const isZero = roundedValue === 0;
		const textColor = isZero ? 'text-text-3' : 'text-text-2';
		return (
			<div className={`mt-2 font-normal ${textColor} flex-row flex justify-end`}>
				<div className="flex-row flex items-center">
					{formatValue(cell.value, "currency", false)}
				</div>
			</div>
		);
	}
	
	if (includeForecast && rowData[`${columnId}_type`] === 'combined') {
		// For combined values, we need to show both measure and forecast with different colors
		const measureValue = rowData[`${columnId}_measure`] || 0;
		const forecastValue = rowData[`${columnId}_forecast`] || 0;
		
		return (
			<div className="mt-2 font-normal flex-row flex justify-end">					
				<div className="flex-row flex items-center space-x-1">
					{measureValue > 0 && (
						<span className="text-text-2">
							{formatValue(measureValue, "currency", false)}
						</span>
					)}
					{forecastValue > 0 && (
						<span className="text-danger">
							{formatValue(forecastValue, "currency", false)}
						</span>
					)}
				</div>
			</div>
		);
	} else {
		// For measure-only values
		const rawValue = cell.value || 0;
		const roundedValue = Math.round((rawValue + Number.EPSILON) * 100) / 100;
		const isZero = roundedValue === 0;
		const textColor = isZero ? 'text-text-3' : 'text-primary';
		return (
			<div className={`mt-2 font-normal ${textColor} flex-row flex justify-end`}>					
				<div className="flex-row flex items-center">
					{formatValue(cell.value, "currency", false)}
				</div>
			</div>
		);
	}
}

const descriptionCell = (cell) => {
	return (
		<div className="mt-2 text-text-2 flex-row flex">
			<div className="flex-row flex items-center">
				{cell.value}
			</div>
		</div>
	)
}

const valueHeader = (header, reportingPeriods, dateGrouping) => {
	const periodNumber = header.column.id.split("C")[1];
	// Extract format string from dateGrouping object if it's an object, otherwise use it directly
	const dateFormat = typeof dateGrouping === 'object' && dateGrouping !== null ? dateGrouping.value : dateGrouping;

	
	return (
		<div className={headerClass}>
			<span data-tooltip-id="instantTooltip" data-tooltip-content="The date where the cost was incurred">
				{dayjs(reportingPeriods[0]).add(periodNumber - 1, getGranularity(dateFormat)).format(dateFormat)}
			</span>
		</div>
	)
}

function getGranularity(dateFormat) {
	var returnValue = "day";

	switch (dateFormat) {
		case "MMM D":
			returnValue = "day";
			break;
		case "YYYY-WW":
			returnValue = "week";
			break;
		case "MMM YYYY":
			returnValue = "month";
			break;
		case "YYYY-Q":
			returnValue = "quarter";
			break;
		case "YYYY":
			returnValue = "year";
			break;
		default:
			break; // the value initialised above is the fallback
	}

	return returnValue;
}

export const AnalyzerDetailedTable = (props) => {
	const { sourceData, grouping, dateGrouping,  includeForecast = false, title } = props;
	// Extract format string from dateGrouping object if it's an object, otherwise use it directly
	const dateFormat = typeof dateGrouping === 'object' && dateGrouping !== null ? dateGrouping.value : dateGrouping;

	const reportingPeriods = determineReportingPeriods(sourceData, getGranularity(dateFormat));
	const data = addTotalColumn(pivotData(sourceData, reportingPeriods, includeForecast), includeForecast);

	const columnDefs = [
		{
			Header: (cell) => (
				<div className={headerClass}>
					<span className="text-left mr-auto" data-tooltip-id="instantTooltip" data-tooltip-content="The name of the cost">
						{grouping}
					</span>
				</div>
			), 
			accessor: "grouping",
			Cell: (cell) => descriptionCell(cell),
			width: 300,
		},
		{
			Header: (cell) => (
				<div className={headerClass}>
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The total cost">Total</span>
				</div>
			),
			accessor: "total",
			Cell: (cell) => valueCell(cell, includeForecast),
			width: 125,
		}
	];
	
	function addColumns(columnDefs, reportingPeriods) {
		var returnValue = columnDefs;

		for (let i = 0; i < reportingPeriods.length; i++) {
			returnValue.push({
				accessor: `C${i + 1}`,
				Header: (header) => valueHeader(header, reportingPeriods, dateFormat),
				Cell: (cell) => valueCell(cell, includeForecast),
				width: 125,
			});
		}

		return returnValue;
	}

	const columns = addColumns(columnDefs, reportingPeriods);

	// Calculate totals for total row
	const calculateTotals = () => {
		if (!data || data.length === 0) return null;
		
		const totals = {};
		
		// Sum up all numeric columns
		columns.forEach(column => {
			const accessor = column.accessor;
			if (accessor === 'grouping') {
				// Grouping column will show "Total" text
				totals[accessor] = 'Total';
			} else {
				// Sum all values for this column
				const sum = data.reduce((acc, row) => {
					let value = 0;
					if (accessor === 'total') {
						value = row.total || 0;
					} else if (accessor.startsWith('C')) {
						value = row[accessor] || 0;
					}
					return acc + (typeof value === 'number' ? value : 0);
				}, 0);
				totals[accessor] = sum;
			}
		});
		
		return totals;
	};

	const totals = calculateTotals();

    return (
		<Fragment>
			<div className="bg-card border border-card-bd p-4 rounded-card mr-6 mt-6">
				<Tooltip />
				<div className="text-text text-lg font-semibold mt-1 mb-6 cursor-default" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
					{title}
				</div>

				<Table columns={columns} data={data} totals={totals} includeForecast={includeForecast} />
			</div>
		</Fragment>
	);
};

function Table({ columns, data, totals = null, includeForecast = false }) {
	const defaultColumn = React.useMemo(() => ({ width: "auto" }), []);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		
		
	} = useTable({ columns, data, defaultColumn }, useBlockLayout);

	return (
		<div className="w-[calc(100vw-225px)] block mr-6 mt-0 pt-6">
			<div className="max-w-full overflow-x-auto overflow-y-hidden">
				<div {...getTableProps()} className="w-full cursor-default min-w-max table-fixed">
					<div className="py-2 border-b border-border pl-4 text-text-2 w-full">
						{headerGroups.map((headerGroup) => (
							<div {...headerGroup.getHeaderGroupProps()} className="tr flex">
							{headerGroup.headers.map((column, columnIndex) => {
								const headerProps = column.getHeaderProps();
								return (
									<div
										{...headerProps}
										className={`th uppercase text-sm ${columnIndex <= 1 ? 'sticky z-10 bg-card shadow-sm' : ''}`}
										style={{
											...headerProps.style,
											...(columnIndex === 0 ? { position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'hsl(var(--card))' } : {}),
											...(columnIndex === 1 ? { position: 'sticky', left: '300px', zIndex: 10, backgroundColor: 'hsl(var(--card))' } : {})
										}}
									>
										{column.render("Header")}
									</div>
								);
							})}
							</div>
						))}
					</div>

					<div {...getTableBodyProps()} className="">
						{rows.map((row, index) => {
							prepareRow(row);

							return (
								<div
									{...row.getRowProps({
									})}
									className="tr border-b flex border-b border-dashed border-border"
								>
									{row.cells.map((cell, cellIndex) => {
										const cellProps = cell.getCellProps();
										return (
											<div
												{...cellProps}
												className={`td truncate pl-4 pt-2 pb-1 text-sm ${cellIndex <= 1 ? 'sticky z-10 bg-card shadow-sm' : ''}`}
												style={{
													...cellProps.style,
													...(cellIndex === 0 ? { position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'hsl(var(--card))' } : {}),
													...(cellIndex === 1 ? { position: 'sticky', left: '300px', zIndex: 10, backgroundColor: 'hsl(var(--card))' } : {})
												}}
											>
												{cell.render("Cell")}
											</div>
										);
									})}
								</div>
							);
						})}
						{/* Total Row */}
						{totals && headerGroups.length > 0 && (
							<div className="tr flex border-b-2 border-solid border-border bg-surface-2">
								{headerGroups[0].headers.map((column, columnIndex) => {
									const headerProps = column.getHeaderProps();
									const columnId = column.id || column.accessor;
									const totalValue = totals[columnId];
									
									return (
										<div
											{...headerProps}
											className={`td truncate pl-4 pt-2 pb-1 text-sm font-semibold ${columnIndex <= 1 ? 'sticky z-10 bg-surface-2 shadow-sm' : ''} ${columnId === 'grouping' ? 'text-text' : ''}`}
											style={{
												...headerProps.style,
												...(columnIndex === 0 ? { position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'hsl(var(--surface-2))' } : {}),
												...(columnIndex === 1 ? { position: 'sticky', left: '300px', zIndex: 10, backgroundColor: 'hsl(var(--surface-2))' } : {})
											}}
										>
											{columnId === 'grouping' ? (
												<div className="mt-2">
													<span>Total</span>
												</div>
											) : (
												<div className={`mt-2 font-semibold ${(totalValue || 0) === 0 ? 'text-text-3' : 'text-text-2'} flex-row flex justify-end`}>
													<div className="flex-row flex items-center">
														{formatValue(totalValue, "currency", false)}
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}